import { CancelOutlined, CheckRounded, HourglassEmpty, QuestionMark } from '@mui/icons-material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
export function formatDateToDDMMYYYY(isoString) {
  if (!isoString) return '';

  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
}
export default function ApprovalWorkflowCard({
  title = '-',
  author = '',
  date = '-',
  description = '-',
  statusChip = '-',
  statusId,
  category = '-',
  approvers,
  onDownload,
  onClick,
  id,
}) {
  const { t } = useTranslation('translations');
  const [showSteps, setShowSteps] = React.useState(false);

  const chipSx = {
    height: 26,
    borderRadius: 999,
    fontWeight: 600,
    '& .MuiChip-label': { px: 1.2 },
  };
  const DONE_GREEN = { main: '#22C55E', dark: '#16A34A' };
  const BLUE = { main: '#3B82F6', dark: '#1D4ED8' };
  const RED = { main: '#EF4444', dark: '#B91C1C' };

  // state: 0=done, 2=waiting/active, 1=rejected (based on your code)
  const getStateStyles = (theme, state) => {
    if (state === 0) {
      return {
        kind: 'done',
        borderColor: alpha(DONE_GREEN.main, 0.55),
        bgcolor: alpha(DONE_GREEN.main, 0.1),
        iconBg: alpha(DONE_GREEN.main, 0.18),
        iconColor: DONE_GREEN.dark,
      };
    }

    if (state === 2) {
      return {
        kind: 'active',
        borderColor: alpha(BLUE.main, 0.45),
        bgcolor: alpha(BLUE.main, 0.08),
        iconBg: alpha(BLUE.main, 0.16),
        iconColor: BLUE.dark,
      };
    }

    if (state === 1) {
      return {
        kind: 'rejected',
        borderColor: alpha(RED.main, 0.45),
        bgcolor: alpha(RED.main, 0.08),
        iconBg: alpha(RED.main, 0.16),
        iconColor: RED.dark,
      };
    }

    return {
      kind: 'unknown',
      borderColor: alpha(theme.palette.divider, 0.9),
      bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'light' ? 0.02 : 0.06),
      iconBg: alpha(theme.palette.text.primary, 0.08),
      iconColor: alpha(theme.palette.text.primary, 0.55),
    };
  };
  const getStatusChipSx = (theme, statusId) => {
    // adjust ids if your backend differs
    if (statusId === 1 || statusId === 6) {
      // done / signed
      return {
        bgcolor: alpha('#22C55E', 0.1),
        color: '#166534',
        border: `1px solid ${alpha('#22C55E', 0.35)}`,
      };
    }

    if (statusId === 2 || statusId === 5) {
      // active / in progress
      return {
        bgcolor: alpha('#3B82F6', 0.08),
        color: '#1D4ED8',
        border: `1px solid ${alpha('#3B82F6', 0.35)}`,
      };
    }

    if (statusId === 3 || statusId === 4) {
      // rejected
      return {
        bgcolor: alpha('#EF4444', 0.08),
        color: '#B91C1C',
        border: `1px solid ${alpha('#EF4444', 0.35)}`,
      };
    }

    // default / unknown
    return {
      bgcolor: alpha(theme.palette.text.primary, 0.04),
      color: theme.palette.text.primary,
      border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    };
  };

  const totalSteps = approvers?.length ?? 0;

  const doneCount = (approvers ?? []).filter((a) => a?.approverStatus === 0).length;
  const rejectedCount = (approvers ?? []).filter((a) => a?.approverStatus === 1).length;

  // consider rejected as "finished" step
  const finishedCount = doneCount + rejectedCount;

  const progressValue = totalSteps ? Math.round((finishedCount / totalSteps) * 100) : 0;

  const stepText = totalSteps
    ? `${t('Stage')} ${Math.min(finishedCount + 1, totalSteps)} / ${totalSteps}`
    : '';
  const percentText = `${progressValue}% ${t('Completed').toLowerCase()}`;
  const hasRejected = (approvers ?? []).some((a) => a?.approverStatus === 1);

  return (
    <Card
      onClick={onClick}
      elevation={0}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: theme.palette.background.paper,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'grey',
        },
      })}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        {/* Top row */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
          justifyContent="space-between"
          spacing={1.5}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <h2 className="mr-2 text-16">{title}</h2>

              <Chip
                label={statusChip}
                sx={(theme) => ({
                  ...chipSx,
                  ...getStatusChipSx(theme, statusId),
                })}
              />

              <Chip
                label={category}
                sx={(theme) => ({
                  ...chipSx,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  color: theme.palette.info.main,
                })}
              />
            </Stack>

            {/* Meta row */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              flexWrap="wrap"
              sx={{ mt: 0.75, color: 'text.secondary' }}
            >
              <>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <>
                    <Typography variant="caption">id: {id}</Typography>
                    <Typography variant="body2">•</Typography>
                  </>
                </Stack>
              </>

              {author && (
                <>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <PersonOutlineRoundedIcon sx={{ fontSize: 18 }} />
                    <>
                      <Tooltip title={t('Creator')} arrow placement="top">
                        <Typography variant="caption">{author}</Typography>
                      </Tooltip>
                      <Typography variant="body2">•</Typography>
                    </>
                  </Stack>
                </>
              )}

              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />
                <Tooltip title={t('CreatedDate')} arrow placement="top">
                  <Typography variant="caption">
                    {moment(date).format('DD.MM.YYYY HH:mm')}
                  </Typography>
                </Tooltip>
              </Stack>
            </Stack>

            {description ? (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {description}
              </Typography>
            ) : null}
          </Box>

          {/* Actions */}
          {/* <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            <IconButton
              onClick={onDownload}
              sx={(theme) => ({
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
              })}
            >
              <DownloadOutlinedIcon />
            </IconButton>
          </Stack> */}
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Progress linear */}
        <Box
          sx={(theme) => ({
            borderRadius: 2,
            bgcolor: alpha(
              theme.palette.text.primary,
              theme.palette.mode === 'light' ? 0.03 : 0.06
            ),
            p: 1.5,
            mb: 1.5,
          })}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption">{stepText}</Typography>
            <Typography variant="caption" color="text.secondary">
              {percentText}
            </Typography>
          </Stack>

          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={(theme) => ({
              height: 7,
              borderRadius: 999,
              mt: 1.25,

              /* Track (background) */
              bgcolor:
                theme.palette.mode === 'light'
                  ? alpha(theme.palette.grey[300], 0.6)
                  : alpha(theme.palette.grey[800], 0.9),

              /* Bar */
              '& .MuiLinearProgress-bar': {
                borderRadius: 999,
                bgcolor: 'green',
              },
            })}
          />
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{
            mt: 1,
            '&:hover': {
              bgcolor: alpha('#2596be', 0.02),
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            setShowSteps((v) => !v);
          }}
        >
          <Typography variant="caption" sx={{ fontSize: 14 }}>
            {t('ApprovalRoute')}
          </Typography>
          <ChevronRightRoundedIcon
            sx={{
              fontSize: 18,
              transition: 'transform 0.2s ease',
              transform: showSteps ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          />
        </Stack>

        {/* Route steps */}
        <Collapse in={showSteps} timeout="auto" unmountOnExit>
          <Stack
            direction={{ xs: 'row', md: 'row' }}
            spacing={1.25}
            sx={{ mt: 2, flexWrap: 'wrap' }}
            alignItems="stretch"
            rowGap={2}
            onClick={(e) => e.stopPropagation()} // so clicks inside won't trigger card click
          >
            {approvers?.map((s, idx) => (
              <React.Fragment key={s.staffId}>
                <RouteStepCard step={s} getStateStyles={getStateStyles} index={idx} />

                {idx !== approvers.length - 1 ? (
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'flex' },
                      alignItems: 'center',
                      px: 0.25,
                      color: 'text.disabled',
                    }}
                  >
                    <ChevronRightRoundedIcon />
                  </Box>
                ) : null}
              </React.Fragment>
            ))}
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
}

function RouteStepCard({ step, getStateStyles }) {
  return (
    <Box
      sx={(theme) => {
        const s = getStateStyles(theme, step.approverStatus);
        return {
          flex: 1,
          minWidth: 250,
          maxWidth: 350,
          borderRadius: 4,
          border: `1px solid ${s.borderColor}`,
          bgcolor: s.bgcolor,
          p: 1.25,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
        };
      }}
    >
      <Box
        sx={(theme) => {
          const s = getStateStyles(theme, step.approverStatus);
          return {
            mt: 0.35,
            width: 22,
            height: 22,
            borderRadius: 999,
            display: 'grid',
            placeItems: 'center',
            bgcolor: s.iconBg,
            color: s.iconColor,
            flex: '0 0 auto',
          };
        }}
      >
        {(() => {
          const kind =
            step.approverStatus === 0
              ? 'done'
              : step.approverStatus === 2
              ? 'active'
              : step.approverStatus === 1
              ? 'rejected'
              : 'unknown';

          if (kind === 'done') return <CheckRounded sx={{ fontSize: 16 }} />;
          if (kind === 'active') return <HourglassEmpty sx={{ fontSize: 16 }} />;
          if (kind === 'rejected') return <CancelOutlined sx={{ fontSize: 16 }} />;
          return <QuestionMark sx={{ fontSize: 16 }} />;
        })()}
      </Box>

      {/* <Avatar
        sx={(theme) => {
          const s = getStateStyles(theme, step.approverStatus);
          return {
            width: 34,
            height: 34,
            bgcolor: s.iconBg,
            color: s.iconColor,
            fontSize: 14,
          };
        }}
      >
        {step.name?.[0]}
      </Avatar> */}

      {/* rest stays same... */}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
          {step.name}
        </Typography>
        {step.role && (
          <Typography
            variant="caption"
            sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}
          >
            {step.role}
          </Typography>
        )}
        {step?.lastModifiedDate ? (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75 }}>
            <AccessTimeRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {formatDateToDDMMYYYY(step.lastModifiedDate)}
            </Typography>
          </Stack>
        ) : step?.updateDate ? (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75 }}>
            <AccessTimeRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {formatDateToDDMMYYYY(step.updateDate)}
            </Typography>
          </Stack>
        ) : null}
        {step.comment ? (
          <Tooltip title={step.comment} arrow placement="top" disableInteractive={false}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mt: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                lineHeight: 1.3,
                cursor: 'help',
              }}
            >
              {step.comment}
            </Typography>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
}
